/**
 * TOSS Web Application - Auth TanStack Query Hooks
 *
 * React hooks for authentication using TanStack Query:
 * - useLogin - Login mutation
 * - useRegister - Registration mutation
 * - useLogout - Logout mutation
 * - useForgotPassword - Request password reset
 * - useResetPassword - Reset password with token
 * - useVerifyMentor - Verify mentor for registration
 * - useCurrentUser - Get current user query
 * - useChangePassword - Change password mutation
 * - useUpdateFcmToken - Update FCM token mutation
 */

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/app/queryClient';
import { useAuthStore } from '@/store/authStore';
import {
  authApi,
  ChangePasswordRequest,
  DeleteFcmTokenRequest,
  UpdateFcmTokenRequest,
} from '@/api/auth.api';
import {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  VerifyMentorRequest,
  VerifyMentorResponse,
  SuccessResponse,
  User,
} from '@/types';
import { getErrorMessage } from '@/api/client';

// ============================================================================
// Types
// ============================================================================

/**
 * Options for useLogin hook
 */
export interface UseLoginOptions {
  /** Callback on successful login */
  onSuccess?: (data: LoginResponse) => void;
  /** Callback on login error */
  onError?: (error: Error) => void;
}

/**
 * Options for useRegister hook
 */
export interface UseRegisterOptions {
  /** Callback on successful registration */
  onSuccess?: (data: RegisterResponse) => void;
  /** Callback on registration error */
  onError?: (error: Error) => void;
}

/**
 * Options for useLogout hook
 */
export interface UseLogoutOptions {
  /** Callback on successful logout */
  onSuccess?: () => void;
  /** Callback on logout error */
  onError?: (error: Error) => void;
}

/**
 * Options for useCurrentUser hook
 */
export interface UseCurrentUserOptions {
  /** Whether the query is enabled */
  enabled?: boolean;
}

// ============================================================================
// Login Hook
// ============================================================================

/**
 * Hook for user login
 *
 * @param options - Callback options
 * @returns Mutation for logging in
 *
 * @example
 * ```tsx
 * function LoginForm() {
 *   const login = useLogin({
 *     onSuccess: () => navigate('/dashboard'),
 *     onError: (error) => toast.error(error.message)
 *   });
 *
 *   const handleSubmit = (data: LoginRequest) => {
 *     login.mutate(data);
 *   };
 *
 *   return (
 *     <form onSubmit={handleSubmit}>
 *       <button disabled={login.isPending}>
 *         {login.isPending ? 'Logging in...' : 'Login'}
 *       </button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useLogin(options?: UseLoginOptions) {
  const queryClient = useQueryClient();
  const setAuth = useAuthStore((state) => state.setAuth);
  const setError = useAuthStore((state) => state.setError);

  return useMutation<LoginResponse, Error, LoginRequest>({
    mutationFn: authApi.login,
    onSuccess: (data) => {
      // Store auth state in Zustand and localStorage
      setAuth(data.user, data.accessToken, data.refreshToken, data.expiresAt);

      // Update current user query cache
      queryClient.setQueryData(queryKeys.auth.me(), data.user);

      // Call user's onSuccess callback
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      const message = getErrorMessage(error);
      setError(message);
      options?.onError?.(error);
    },
  });
}

// ============================================================================
// Register Hook
// ============================================================================

/**
 * Hook for user registration
 *
 * @param options - Callback options
 * @returns Mutation for registering
 *
 * @example
 * ```tsx
 * function RegisterForm() {
 *   const register = useRegister({
 *     onSuccess: () => {
 *       toast.success('Registration successful! Please wait for approval.');
 *       navigate('/login');
 *     }
 *   });
 *
 *   const handleSubmit = (data: RegisterRequest) => {
 *     register.mutate(data);
 *   };
 * }
 * ```
 */
export function useRegister(options?: UseRegisterOptions) {
  return useMutation<RegisterResponse, Error, RegisterRequest>({
    mutationFn: authApi.register,
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      options?.onError?.(error);
    },
  });
}

// ============================================================================
// Logout Hook
// ============================================================================

/**
 * Hook for user logout
 *
 * @param options - Callback options
 * @returns Mutation for logging out
 *
 * @example
 * ```tsx
 * function LogoutButton() {
 *   const logout = useLogout({
 *     onSuccess: () => navigate('/login')
 *   });
 *
 *   return (
 *     <button onClick={() => logout.mutate()} disabled={logout.isPending}>
 *       Logout
 *     </button>
 *   );
 * }
 * ```
 */
export function useLogout(options?: UseLogoutOptions) {
  const queryClient = useQueryClient();
  const logout = useAuthStore((state) => state.logout);

  return useMutation<void, Error, void>({
    mutationFn: authApi.logout,
    onSuccess: () => {
      // Clear Zustand auth state
      logout();

      // Clear all cached queries
      queryClient.clear();

      options?.onSuccess?.();
    },
    onError: (error) => {
      // Still clear local state on error
      logout();
      queryClient.clear();

      options?.onError?.(error);
    },
  });
}

// ============================================================================
// Forgot Password Hook
// ============================================================================

/**
 * Hook for requesting password reset email
 *
 * @returns Mutation for forgot password
 *
 * @example
 * ```tsx
 * function ForgotPasswordForm() {
 *   const forgotPassword = useForgotPassword();
 *
 *   const handleSubmit = (email: string) => {
 *     forgotPassword.mutate({ email }, {
 *       onSuccess: () => toast.success('Check your email for reset instructions')
 *     });
 *   };
 * }
 * ```
 */
export function useForgotPassword() {
  return useMutation<SuccessResponse, Error, ForgotPasswordRequest>({
    mutationFn: authApi.forgotPassword,
  });
}

// ============================================================================
// Reset Password Hook
// ============================================================================

/**
 * Hook for resetting password with email token
 *
 * @returns Mutation for reset password
 *
 * @example
 * ```tsx
 * function ResetPasswordForm({ email, token }: { email: string; token: string }) {
 *   const resetPassword = useResetPassword();
 *
 *   const handleSubmit = (newPassword: string) => {
 *     resetPassword.mutate({ email, token, newPassword }, {
 *       onSuccess: () => {
 *         toast.success('Password reset successfully');
 *         navigate('/login');
 *       }
 *     });
 *   };
 * }
 * ```
 */
export function useResetPassword() {
  return useMutation<SuccessResponse, Error, ResetPasswordRequest>({
    mutationFn: authApi.resetPassword,
  });
}

// ============================================================================
// Verify Mentor Hook
// ============================================================================

/**
 * Hook for verifying mentor during registration
 *
 * @returns Mutation for mentor verification
 *
 * @example
 * ```tsx
 * function MentorSelect() {
 *   const verifyMentor = useVerifyMentor();
 *
 *   const handleSearch = (mentorName: string) => {
 *     verifyMentor.mutate({ fullName: mentorName }, {
 *       onSuccess: (data) => {
 *         if (data.found && data.mentor) {
 *           setSelectedMentor(data.mentor);
 *         }
 *       }
 *     });
 *   };
 * }
 * ```
 */
export function useVerifyMentor() {
  return useMutation<VerifyMentorResponse, Error, VerifyMentorRequest>({
    mutationFn: authApi.verifyMentor,
  });
}

// ============================================================================
// Current User Hook
// ============================================================================

/**
 * Hook for fetching current authenticated user
 *
 * @param options - Query options
 * @returns Query for current user
 *
 * @example
 * ```tsx
 * function UserProfile() {
 *   const { data: user, isLoading, error } = useCurrentUser();
 *
 *   if (isLoading) return <Spinner />;
 *   if (error) return <Error message={error.message} />;
 *
 *   return <div>Welcome, {user?.fullName}</div>;
 * }
 * ```
 */
export function useCurrentUser(options?: UseCurrentUserOptions) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const updateUser = useAuthStore((state) => state.updateUser);

  const query = useQuery<User, Error>({
    queryKey: queryKeys.auth.me(),
    queryFn: authApi.getCurrentUser,
    enabled: options?.enabled ?? isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });

  // Update Zustand store when user data is fetched
  // This ensures store stays in sync with server
  // Using useEffect avoids state updates during render phase
  useEffect(() => {
    if (query.data) {
      updateUser(query.data);
    }
  }, [query.data, updateUser]);

  return query;
}

// ============================================================================
// Change Password Hook
// ============================================================================

/**
 * Hook for changing current user's password
 *
 * @returns Mutation for password change
 *
 * @example
 * ```tsx
 * function ChangePasswordForm() {
 *   const changePassword = useChangePassword();
 *
 *   const handleSubmit = (data: ChangePasswordRequest) => {
 *     changePassword.mutate(data, {
 *       onSuccess: () => toast.success('Password changed successfully'),
 *       onError: (error) => toast.error(error.message)
 *     });
 *   };
 * }
 * ```
 */
export function useChangePassword() {
  return useMutation<SuccessResponse, Error, ChangePasswordRequest>({
    mutationFn: authApi.changePassword,
  });
}

// ============================================================================
// Update FCM Token Hook
// ============================================================================

/**
 * Hook for updating FCM token for push notifications
 *
 * @returns Mutation for FCM token update
 *
 * @example
 * ```tsx
 * function usePushNotifications() {
 *   const updateFcmToken = useUpdateFcmToken();
 *
 *   useEffect(() => {
 *     // Get FCM token from Firebase
 *     getMessagingToken().then((token) => {
 *       if (token) {
 *         updateFcmToken.mutate({ fcmToken: token });
 *       }
 *     });
 *   }, []);
 * }
 * ```
 */
export function useUpdateFcmToken() {
  const updateUser = useAuthStore((state) => state.updateUser);

  return useMutation<SuccessResponse, Error, UpdateFcmTokenRequest>({
    mutationFn: authApi.updateFcmToken,
    onSuccess: (_data, variables) => {
      // Update user in store with new FCM token
      updateUser({ fcmToken: variables.fcmToken });
    },
  });
}

// ============================================================================
// Delete FCM Token Hook
// ============================================================================

/**
 * Hook for unregistering an FCM token on logout.
 *
 * Call this from the logout flow BEFORE `/api/auth/logout`, while the JWT is
 * still valid. Removes only the current device's token — other devices
 * (e.g. the user's phone) remain subscribed to push notifications.
 */
export function useDeleteFcmToken() {
  return useMutation<void, Error, DeleteFcmTokenRequest>({
    mutationFn: authApi.deleteFcmToken,
  });
}

// ============================================================================
// Combined Auth Hooks Export
// ============================================================================

/**
 * All auth-related hooks
 *
 * @example
 * ```ts
 * import { useLogin, useLogout, useCurrentUser } from '@/hooks/api/useAuth';
 * ```
 */
export const authHooks = {
  useLogin,
  useRegister,
  useLogout,
  useForgotPassword,
  useResetPassword,
  useVerifyMentor,
  useCurrentUser,
  useChangePassword,
  useUpdateFcmToken,
  useDeleteFcmToken,
} as const;

export default authHooks;
